/* 
As a user I want to be able to edit the data associated with my organization
So that I can ensure that the connected to my organization is accurate.
*/

//Packages
import React, { useState, useEffect, useCallback, useContext } from "react";
import imageCompression from "browser-image-compression";
import Cropper from "react-easy-crop";
import axios from "axios";
//Material UI Components
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Slider,
  Modal,
  Stack,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

//Context
import { userContext } from "../../Context";
//Utils
import getCroppedImage from "../../utils/cropImage";
import PlaceholderImage from "../../assets/images/placeholderLg.png";
import { Route } from "../../utils/config";

//Styles
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  p: 4,
  backgroundColor: "#ffffff",
};

export default function Edit() {
  const { user } = useContext(userContext);
  const [selectedFile, setSelectedFile] = useState({
    partnerId: user.partnerId,
    base64ImageText: "",
    extension: "string",
  });
  const [dropdowns, setDropdowns] = useState({
    specialties: [""],
    geographicPresence: [],
  });

  //Form object
  const [form, setForm] = useState({
    id: user.partnerId,
    companyName: "",
    contactFirstName: "",
    contactLastName: "",
    email: "",
    cellNumber: "",
    altContact1: "",
    altContact2: "",
    country: "",
    region: "",
    postalCode: "",
    webSite: "",
    servicesProvided: "",
    geographicalExpertise: "",
    supportedRegions: "",
    numberOfEmployees: 0,
    ein: "",
    specialty: "",
    yearEstablished: 0,
    description: "",
    isApproved: true,
    isDeleted: false,
    overview: "",
  });
  const [detailForm, setDetailForm] = useState({
    specialties: [],
    presence: [],
  });
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [selectedPresence, setSelectedPresence] = useState([]);

  const [isSaved, setIsSaved] = useState(false);
  const [isImageSelected, setImageSelected] = useState(false);

  //States for image cropping component
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [cropMode, toggleCropMode] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  //Obtain user's info on page render
  const getUserInfo = useCallback(() => {
    let store = JSON.parse(localStorage.getItem("login"));

    axios
      .get(`${Route}/Partners/GetPartnerDisplayDetails`, {
        params: {
          partnerId: user.partnerId,
        },
        headers: { Authorization: `Bearer ${store.token}` },
      })
      .then((res) => {
        setForm({
          ...res.data.partner,
          id: user.partnerId,
        });
        if (res.data.specialties.length > 0) {
          setSelectedSpecialties(
            res.data.specialties.map((specialty) => specialty.specialtyId)
          );
        }
        if (res.data.presence.length > 0) {
          setSelectedPresence(
            res.data.presence.map((presence) => presence.presenceId)
          );
        }
      })
      .catch((err) => {
        throw err;
      });
  }, [user.partnerId]);

  //Loads dropdowns for project
  const getDropdownValues = () => {
    axios
      .get(`${Route}/Application/GetSystemData`)
      .then((res) => {
        setDropdowns({
          specialties: res.data.specialties,
          geographicPresence: res.data.geographicPresence,
        });
      })
      .catch((err) => {
        throw err;
      });
  };

  const getData = async () => {
    await getDropdownValues();
  };

  useEffect(() => {
    getUserInfo();
    getData();
  }, []);

  //Function to define cropped area after user is finished selecting
  //Their desired crop area
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageChange = async (event) => {
    let files = event.target.files;

    if (files[0].size < 500 * 1024) {
      const imageFile = event.target.files[0];
      const imageType = event.target.files[0].type.replace(/(.*)\//g, "");

      setSelectedFile({ ...selectedFile, extension: imageType });
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(imageFile, options);

        await saveFile(compressedFile);
      } catch (error) {}
    } else {
      window.alert("File too big.");
    }
  };

  const handleSaveCrop = async () => {
    toggleCropMode(false);
    const newImage = await getCroppedImage(
      selectedFile.base64ImageText,
      croppedAreaPixels
    );
    setSelectedFile({
      ...selectedFile,
      base64ImageText: newImage,
      extension: "png",
    });
    setImageSelected(true);
  };

  const saveFile = (file) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setSelectedFile({
        ...selectedFile,
        base64ImageText: reader.result,
      });
      toggleCropMode(true);
    };
  };

  const handleClose = () => {
    toggleCropMode(false);
    setSelectedFile({
      partnerId: user.partnerId,
      base64ImageText: "",
      extension: "string",
    });
  };

  const handleSubmit = (form, image) => {
    let store = JSON.parse(localStorage.getItem("login"));

    axios
      .post(`${Route}/Partners/SavePartner`, JSON.stringify(form), {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${store.token}`,
        },
      })
      .then((res) => {
        setIsSaved(true);
      })
      .catch((err) => {
        throw err;
      });

    axios
      .post(`${Route}/Partners/SavePartnerLogo`, JSON.stringify(image), {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${store.token}`,
        },
      })
      .then((res) => {})
      .catch((err) => {
        throw err;
      });

    axios
      .post(
        `${Route}/Partners/SavePartnerDetails`,
        JSON.stringify({
          ...detailForm,
          partner: {
            ...form,
          },
        }),
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${store.token}`,
          },
        }
      )
      .then((res) => {
        setIsSaved(true);
      })
      .catch((err) => {
        throw err;
      });
  };

  const handleInputChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  return (
    <Grid container item xs={11} spacing={2} mt={3}>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(form, selectedFile);
        }}
      >
        <Grid item xs={12} mb={3}>
          <Typography variant="h5">Edit Profile</Typography>
        </Grid>
        <Grid container item justifyContent="center" spacing={6} xs={12}>
          <Grid container item spacing={2} xs={12} md={8}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="companyName"
                value={form.companyName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="contactFirstName"
                value={form.contactFirstName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="contactLastName"
                value={form.contactLastName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cell Number"
                name="cellNumber"
                value={form.cellNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alternative Contact #1"
                name="altContact1"
                value={form.altContact1}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alternative Contact #2"
                name="altContact2"
                value={form.altContact2}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Country"
                name="country"
                value={form.country}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Region"
                name="region"
                value={form.region}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                label="Postal Code"
                name="postalCode"
                value={form.postalCode}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Website"
                name="webSite"
                value={form.webSite}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Services Provided"
                name="servicesProvided"
                value={form.servicesProvided}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Geographical Expertise"
                name="geographicalExpertise"
                value={form.geographicalExpertise}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="specialties">
                  Specialities & Area of Expertise
                </InputLabel>
                <Select
                  labelId="specialties"
                  label="Specialities & Area of Expertise"
                  value={selectedSpecialties}
                  name="specialties"
                  multiple
                  onChange={(e) => {
                    setSelectedSpecialties(e.target.value);
                    let specialties = [];
                    e.target.value.forEach((item) => {
                      const itemValue = {
                        partnerId: form.id,
                        specialtyId: item,
                        partner: "",
                        specialty: "",
                      };
                      specialties.push(itemValue);
                    });
                    setDetailForm({
                      ...detailForm,
                      specialties,
                    });
                  }}
                >
                  {dropdowns.specialties.map((specialty) => {
                    return (
                      <MenuItem key={specialty.id} value={specialty.id}>
                        {specialty.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="presence">Geographic Presence</InputLabel>
                <Select
                  labelId="presence"
                  label="Geographic Presence"
                  value={selectedPresence}
                  name="specialties"
                  multiple
                  onChange={(e) => {
                    setSelectedPresence(e.target.value);
                    let presenceArray = [];
                    e.target.value.forEach((item) => {
                      const itemValue = {
                        partnerId: form.id,
                        presenceId: item,
                        partner: "",
                        presence: "",
                      };
                      presenceArray.push(itemValue);
                    });
                    setDetailForm({
                      ...detailForm,
                      presence: presenceArray,
                    });
                  }}
                >
                  {dropdowns.geographicPresence.map((presence) => {
                    return (
                      <MenuItem key={presence.id} value={presence.id}>
                        {presence.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="# of Employees"
                name="numberOfEmployees"
                value={form.numberOfEmployees}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="EIN"
                name="ein"
                value={form.ein}
                onChange={handleInputChange}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Overview"
                name="description"
                value={form.description}
                onChange={handleInputChange}
              />
            </Grid> */}
            <Grid container item xs={12} alignItems="center">
              <Button variant="contained" component="label">
                Upload Logo
                <input type="file" hidden onChange={handleImageChange} />
              </Button>
              {isImageSelected ? (
                <Typography marginLeft={2}>Image selected</Typography>
              ) : (
                ""
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Button variant="contained" size="large" type="submit">
                Save Company
              </Button>
            </Grid>
            <Grid
              container
              item
              xs={12}
              justifyContent={"center"}
              visibility={isSaved ? "visible" : "hidden"}
            >
              <Typography variant="body1">Company Saved</Typography>
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={12}
            md={4}
            spacing={2}
            columnSpacing={2}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Stack width="100%" py={2}>
              <TextField
                fullWidth
                placeholder="Company Overview"
                name="description"
                value={form.description}
                onChange={handleInputChange}
                multiline
                maxRows={6}
                minRows={6}
              />
            </Stack>
            <Card sx={{ maxWidth: 345 }}>
              <CardMedia
                component="img"
                height="140"
                image={selectedFile.base64ImageText || PlaceholderImage}
                alt="green iguana"
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  {`${form.contactFirstName} ${form.contactLastName}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.altContact1}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" color="primary">
                  View Profile
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Modal open={cropMode} onClose={handleClose} sx={{ outline: 0 }}>
        <Box component="div" sx={modalStyle} width={{ xs: 200, md: 400 }}>
          <Grid container>
            <Grid item xs={12} position={"relative"} height={"400px"}>
              <Cropper
                image={selectedFile.base64ImageText}
                crop={crop}
                zoom={zoom}
                minZoom={0.75}
                aspect={0.5 / 0.5}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                zoomWithScroll={false}
                restrictPosition={false}
                showGrid={false}
              ></Cropper>
            </Grid>
            <Grid item xs={12} my={2}>
              Zoom:
              <Slider
                value={zoom}
                min={0.75}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
              ></Slider>
            </Grid>
            <Grid container item xs={12} justifyContent="center">
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveCrop}
              >
                Crop Image
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  );
}
